import { useState } from "react";
import { Link, useParams } from "react-router";
import { get } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { Video } from "../api/types";
import { Shell } from "../components/Shell";
import { Bar, Dot, ErrorLine } from "../components/ui";
import { useIsDesktop } from "../lib/device";
import { fmtDate, fmtDuration } from "../lib/format";
import { videoStatus } from "../lib/status";
import { VideoCard } from "./Videos";
import { MatchPage } from "./VideoPage";

export type BetaSubmission = Video & { uploader_email: string };
const BASE = "/admin/beta-submissions";

export default function AdminBetaSubmissions() {
  const desktop = useIsDesktop();
  const [cursors, setCursors] = useState([""]);
  const cursor = cursors[cursors.length - 1]!;
  return <SubmissionPage key={cursor} cursor={cursor} desktop={desktop}
    previous={cursors.length > 1 ? () => setCursors(cursors.slice(0, -1)) : undefined}
    next={value => setCursors([...cursors, value])} />;
}

function SubmissionPage({ cursor, desktop, previous, next }: { cursor: string; desktop: boolean; previous: (() => void) | undefined; next: (cursor: string) => void }) {
  const list = useLoad(() => get<{ items: BetaSubmission[]; next_cursor: string | null }>(`/ops/beta/submissions?limit=60${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`), [cursor]);
  const items = list.data?.items ?? [];
  usePoll(list.reload, 8000, items.some(video => !videoStatus(video, "pro").terminal));
  return (
    <Shell brand active="Beta submissions">
      <div className="p" style={{ paddingTop: desktop ? 48 : 12, paddingBottom: 40 }} data-testid="beta-submissions">
        <div className="row between"><h1>Beta submissions</h1><Link className="btn ghost sm" to="/admin/pipeline">View pipeline</Link></div>
        <p className="sub mt10">Review submitted matches, clips and players.</p>
        <ErrorLine error={list.error} />
        {list.error && <button className="btn ghost mt16" onClick={() => void list.reload()}>Try again</button>}
        {list.loading && !list.data && <p className="tiny mt24">Loading…</p>}
        {list.data && !items.length && <p className="sub mt32">No beta submissions yet.</p>}
        <div className={desktop ? "grid3 mt32" : ""}>
          {items.map(video => <VideoCard key={video.id} v={video} email={video.uploader_email} plan="pro" desktop={desktop} to={`${BASE}/${video.id}`} />)}
        </div>
        <div className="row mt24">
          {previous && <button className="btn ghost" onClick={previous}>Previous</button>}
          {list.data?.next_cursor && <button className="btn ghost" onClick={() => next(list.data!.next_cursor!)}>Next submissions</button>}
        </div>
      </div>
    </Shell>
  );
}

export function AdminBetaSubmission() {
  const { id = "" } = useParams();
  const detail = useLoad(() => get<{ video: BetaSubmission }>(`/ops/beta/submissions/${id}`).then(response => response.video), [id]);
  const video = detail.data;
  usePoll(detail.reload, 5000, !!video && !videoStatus(video, "pro").terminal);
  if (video && (video.state === "ready" || (video.state === "held" && video.review))) {
    return <MatchPage key={id} v={video} reload={detail.reload} betaReview />;
  }
  return (
    <Shell back={BASE}>
      <div className="p" style={{ paddingTop: 32, paddingBottom: 40 }} data-testid="beta-submission">
        <Link className="tiny u" to={BASE}>Beta submissions</Link>
        <ErrorLine error={detail.error} />
        {!video && !detail.error && <p className="tiny">Loading…</p>}
        {video && <>
          <p className="tiny">{[fmtDate(video.created_at), fmtDuration(video.duration_ms)].filter(Boolean).join(" · ")}</p>
          <h1 className="mt6">{video.title}</h1>
          <p className="sub mt10" style={{ overflowWrap: "anywhere" }} data-testid="submission-email">{video.uploader_email}</p>
          <SubmissionStatus video={video} />
        </>}
      </div>
    </Shell>
  );
}

function SubmissionStatus({ video }: { video: Video }) {
  const status = videoStatus(video, "pro");
  return <div className="mt24"><p><Dot kind={status.dot} />{status.headline}</p><p className="sub mt10">{status.explain}</p><div className="mt16"><Bar value={status.progress} /></div></div>;
}
